import type { MemoVisibility } from '@buakei/types';
import { Button } from '@buakei/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookmarkPlus } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { memoApi } from '@/services/api';

/**
 * Save an answer as a research memo in the active workspace (ADR-024). The server copies the
 * answer and its sources from the saved chat; only the chat, the answer's position, a title
 * and who may read it are sent from here.
 */
export function SaveMemo({
  tokens,
  workspace,
  chatId,
  turn,
  question,
}: {
  tokens: IdentityClient;
  workspace: { id: string; name: string; kind: string };
  chatId: string;
  turn: number;
  question: string;
}) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const shareId = useId();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(
    question.length > 120 ? `${question.slice(0, 117)}…` : question,
  );
  const [visibility, setVisibility] = useState<MemoVisibility>('private');
  const save = useMutation({
    mutationFn: () => memoApi.create(tokens, workspace.id, { chatId, turn, title, visibility }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['memos', workspace.id] }),
  });

  if (save.data) {
    return (
      <p role="status" className="text-sm text-ink-900">
        Saved as a memo in {workspace.name}.{' '}
        <Link
          to={`/professional/memos/${encodeURIComponent(save.data.id)}`}
          className="font-medium text-navy-900 underline"
        >
          Open the memo
        </Link>
      </p>
    );
  }
  if (!open) {
    return (
      <Button variant="secondary" size="sm" className="self-start" onClick={() => setOpen(true)}>
        <BookmarkPlus aria-hidden /> Save as memo
      </Button>
    );
  }
  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-line-200 bg-white p-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={titleId} className="text-sm font-medium text-ink-900">
          Memo title
        </label>
        <input
          id={titleId}
          required
          minLength={3}
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-10 rounded-md border border-line-200 bg-white px-3 text-ink-900"
        />
      </div>
      {workspace.kind === 'firm' ? (
        <div className="flex flex-col gap-1">
          <label htmlFor={shareId} className="text-sm font-medium text-ink-900">
            Who can read it
          </label>
          <select
            id={shareId}
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as MemoVisibility)}
            className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
          >
            <option value="private">Only me</option>
            <option value="workspace">Lawyers in {workspace.name}</option>
          </select>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={save.isPending || title.trim().length < 3}>
          {save.isPending ? 'Saving…' : `Save to ${workspace.name}`}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {save.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {save.error.message}
        </p>
      ) : null}
    </form>
  );
}
